'use client';

import { useState } from 'react';

import type { BlogCategory } from '@eliadmin/core/src/api/blogs/blogs-types';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { BlogCard } from './blog-card';
import { BlogCardSkeleton } from './blog-card-skeleton';
import { Button } from '../ui/button';

const ALL_CATEGORIES_TAB = 'all';

export function BlogsList({ locale, categories }: { locale: string; categories: BlogCategory[] }) {
  const { t } = useTranslations();

  const [activeTab, setActiveTab] = useState(ALL_CATEGORIES_TAB);
  const activeTabId = activeTab === ALL_CATEGORIES_TAB ? undefined : Number(activeTab);

  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, activeTab],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchBlogs({
        locale,
        query: {
          page: pageParam,
          results: 9,
          order: 'desc',
          orderBy: 'created_at',
          where: activeTabId ? { categories: { equals: activeTabId } } : undefined,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const blogs = blogsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
  const isLoading = blogsQuery.isLoading;

  return (
    <section
      className={cn(
        'custom-section',
        'pb-8 pr-0 pt-8 sm:pb-12 sm:pt-10 md:pb-16 md:pt-14 lg:pb-32 lg:pt-16 xl:pb-36 xl:pt-[72px]',
      )}
    >
      <Tabs
        value={activeTab}
        defaultValue={activeTab}
        onValueChange={value => setActiveTab(value)}
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-8 sm:gap-10 lg:gap-12',
        )}
      >
        <TabsList
          className={cn(
            'hide-scroll-bar',
            'flex h-fit w-full flex-nowrap items-start justify-start gap-6 overflow-scroll rounded-none bg-transparent py-0 lg:gap-8',
          )}
        >
          <TabsTrigger
            value={ALL_CATEGORIES_TAB}
            className={cn(
              'ea-blogs__tabs-trigger',
              'm-0 p-0 text-lg font-medium tracking-tighter sm:text-xl lg:text-2xl xl:text-[28px]',
            )}
          >
            {t('text1947')}
          </TabsTrigger>
          {categories.map(category => (
            <TabsTrigger
              value={String(category.id)}
              key={category.id}
              className={cn(
                'ea-blogs__tabs-trigger',
                'm-0 p-0 text-lg font-medium tracking-tighter sm:text-xl lg:text-2xl xl:text-[28px]',
              )}
            >
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent
          value={activeTab}
          className="grid w-full grid-cols-1 gap-x-6 gap-y-8 pr-4 sm:grid-cols-2 sm:gap-y-10 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-12"
        >
          {blogsQuery.error && (
            <p className="col-span-1 pt-8 text-center text-base font-medium sm:col-span-2 sm:text-lg lg:col-span-3 xl:text-xl">
              {t('text2364')}
            </p>
          )}
          {isLoading ? (
            [...Array(9)].map((_, index) => <BlogCardSkeleton key={index} />)
          ) : blogs.length > 0 ? (
            <>
              {blogs.map(blog => (
                <BlogCard key={blog.id} blog={blog} />
              ))}
            </>
          ) : (
            <p className="col-span-1 pt-8 text-center text-base font-medium sm:col-span-2 sm:text-lg lg:col-span-3 xl:text-xl">
              {t('text2376')}
            </p>
          )}
        </TabsContent>
        {[ALL_CATEGORIES_TAB, ...categories.map(category => String(category.id))]
          .filter(value => value !== activeTab)
          .map(value => <TabsContent key={value} value={value} forceMount hidden />)}
        <div
          className={cn('flex w-full items-center justify-center', {
            hidden: !blogsQuery.hasNextPage,
          })}
        >
          <Button
            onClick={() => blogsQuery.fetchNextPage()}
            isLoading={blogsQuery.isFetchingNextPage}
            className="w-full sm:w-fit"
            variant={'outline'}
          >
            {t('text3209')}
          </Button>
        </div>
      </Tabs>
    </section>
  );
}
